import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, type Location } from 'react-router';
import type { Permission } from '@wr/shared';
import { Shell } from './components/Shell.js';
import { Dialog, Notice, PageHead } from './components/ui.js';
import { SCREENS, SETTINGS_TOOLS } from './modules.js';
import { DIALOG_PATHS, NAV } from './nav.js';
import { OfflineProvider } from './offline/context.js';
import { useSession, useSignedIn } from './session.js';
import { ToastProvider } from './components/Toast.js';
import { Account } from './screens/Account.js';
import { AddContact } from './screens/AddContact.js';
import { Changes } from './screens/Changes.js';
import { Code } from './screens/Code.js';
import { ForgotPassword } from './screens/ForgotPassword.js';
import { Home } from './screens/Home.js';
import { ResetPassword } from './screens/ResetPassword.js';
import { Settings } from './screens/Settings.js';
import { Soon } from './screens/Soon.js';
import { SetUpApp } from './screens/SetUpApp.js';
import { SignIn } from './screens/SignIn.js';
import { Welcome } from './screens/Welcome.js';
import { HandTheTablet } from './screens/capture/HandTheTablet.js';
import { ProspectPage } from './screens/packages/ProspectPage.js';
import { Audit } from './screens/admin/Audit.js';
import { InviteUser } from './screens/admin/InviteUser.js';
import { UserPage } from './screens/admin/UserPage.js';
import { Users } from './screens/admin/Users.js';

const NEXT_KEY = 'wr-next-path';

/** Remembers the page asked for before sign-in (a per-tab convenience only). */
function rememberNext(path: string) {
  try {
    if (path !== '/' && path.startsWith('/')) sessionStorage.setItem(NEXT_KEY, path);
  } catch {
    // Storage unavailable: the person lands on the home page instead.
  }
}

function readNext(): string {
  try {
    const next = sessionStorage.getItem(NEXT_KEY);
    return next?.startsWith('/') && !next.startsWith('//') ? next : '/';
  } catch {
    return '/';
  }
}

function forgetNext() {
  try {
    sessionStorage.removeItem(NEXT_KEY);
  } catch {
    // Nothing stored.
  }
}

/** Each sign-in step has its own screen; every other address leads to it. */
export function App() {
  const { status, refresh } = useSession();
  const location = useLocation();

  // The prospect page of a package: public, with its own email gate (brief section 7.6).
  if (location.pathname.startsWith('/p/')) {
    return (
      <Routes>
        <Route path="/p/:token" element={<ProspectPage />} />
      </Routes>
    );
  }

  if (status === null) {
    return (
      <main className="auth">
        <div className="spinner" aria-label="Loading" />
      </main>
    );
  }
  if (status === 'offline' || status === 'offline_expired') {
    return (
      <main className="auth">
        <img className="logo" src="/logo.png" alt="Waste Robotics" width={240} height={47} />
        <h1 className="page">Sales Portal</h1>
        {status === 'offline' ? (
          <Notice kind="error">No connection. Signing in needs the network.</Notice>
        ) : (
          <Notice kind="error">
            Your offline session has ended. Connect to the network and sign in again: everything
            saved on this device stays and is sent after you sign in.
          </Notice>
        )}
        <button type="button" className="btn primary" onClick={() => void refresh()}>
          Try again
        </button>
      </main>
    );
  }

  const linkScreens = (
    <>
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/reset-password" element={<ResetPassword />} />
    </>
  );

  switch (status.session.state) {
    case 'signed_out':
      return (
        <Routes>
          {linkScreens}
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="*" element={<ToSignIn path={location.pathname + location.search} />} />
        </Routes>
      );
    case 'code':
      return (
        <Routes>
          {linkScreens}
          <Route path="/sign-in/code" element={<Code />} />
          <Route path="*" element={<Navigate to="/sign-in/code" replace />} />
        </Routes>
      );
    case 'setup':
      return (
        <Routes>
          {linkScreens}
          <Route path="/set-up-app" element={<SetUpApp />} />
          <Route path="*" element={<Navigate to="/set-up-app" replace />} />
        </Routes>
      );
    case 'signed_in':
      return (
        <Routes>
          {linkScreens}
          <Route path="/sign-in/*" element={<AfterSignIn />} />
          <Route path="/set-up-app" element={<AfterSignIn />} />
          <Route path="/forgot-password" element={<Navigate to="/" replace />} />
          <Route path="*" element={<SignedIn />} />
        </Routes>
      );
  }
}

function ToSignIn({ path }: { path: string }) {
  useEffect(() => {
    if (path !== '/sign-in') rememberNext(path);
  }, [path]);
  return <Navigate to="/sign-in" replace />;
}

/** Back to the page asked for before signing in. */
function AfterSignIn() {
  const [next] = useState(readNext);
  useEffect(forgetNext, []);
  return <Navigate to={next} replace />;
}

function SignedIn() {
  const location = useLocation();
  // A window (Add contact) shows over the page it was opened from; opened
  // directly (a link, a reload), it shows over the home page.
  const dialog = DIALOG_PATHS.includes(location.pathname);
  const background = (location.state as { background?: Location } | null)?.background;
  const pageLocation = dialog ? (background ?? { ...location, pathname: '/' }) : location;
  return (
    <OfflineProvider>
      <ToastProvider>
        <Shell>
          <Routes location={pageLocation}>
            <Route path="/" element={<Home />} />
            <Route path="/account" element={<Account />} />
            <Route path="/sync" element={<Changes />} />
            {/* Hand the tablet (brief section 6.4): the visitor's own screen. */}
            <Route
              path="/visitor"
              element={
                <Allowed permission="contacts.handoff">
                  <HandTheTablet />
                </Allowed>
              }
            />
            <Route
              path="/settings"
              element={
                <AllowedIf allowed={NAV.settings.allowed}>
                  <Settings />
                </AllowedIf>
              }
            />
            {Object.values(NAV)
              .filter((item) => item.step !== undefined)
              .map((item) => {
                // The module's screen once built (modules.tsx), else "Not built yet".
                const Screen = SCREENS[item.key];
                return (
                  <Route
                    key={item.key}
                    path={Screen ? `${item.path}/*` : item.path}
                    element={
                      <AllowedIf allowed={item.allowed}>
                        {Screen ? <Screen /> : <Soon item={item} />}
                      </AllowedIf>
                    }
                  />
                );
              })}
            {SETTINGS_TOOLS.map(({ path, allowed, screen: Screen }) =>
              Screen ? (
                <Route
                  key={path}
                  path={`${path}/*`}
                  element={
                    <AllowedIf allowed={allowed}>
                      <Screen />
                    </AllowedIf>
                  }
                />
              ) : null,
            )}
            <Route
              path="/admin/users"
              element={
                <Allowed permission="admin.users.read">
                  <Users />
                </Allowed>
              }
            />
            <Route
              path="/admin/users/new"
              element={
                <Allowed permission="admin.users.manage">
                  <InviteUser />
                </Allowed>
              }
            />
            <Route
              path="/admin/users/:id"
              element={
                <Allowed permission="admin.users.read">
                  <UserPage />
                </Allowed>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <Allowed permission="admin.audit.read">
                  <Audit />
                </Allowed>
              }
            />
            <Route
              path="*"
              element={
                <PageHead
                  title="Page not found"
                  lede="This address does not exist in the portal."
                />
              }
            />
          </Routes>
        </Shell>
        {location.pathname === NAV.addContact.path && (
          <AllowedIf allowed={NAV.addContact.allowed} dialog>
            <AddContact />
          </AllowedIf>
        )}
        {/* The other windows of the navigation (Add lead), once their module is built. */}
        {Object.values(NAV)
          .filter((item) => item.key !== 'addContact' && DIALOG_PATHS.includes(item.path))
          .map((item) => {
            const Screen = SCREENS[item.key];
            return Screen && location.pathname === item.path ? (
              <AllowedIf key={item.key} allowed={item.allowed} dialog>
                <Screen />
              </AllowedIf>
            ) : null;
          })}
      </ToastProvider>
    </OfflineProvider>
  );
}

const NOT_ALLOWED =
  'Your roles do not give access to this page. Ask an administrator if you need it.';

/** The screen when the user holds the permission; the server checks again on every call. */
function Allowed({ permission, children }: { permission: Permission; children: ReactNode }) {
  return <AllowedIf allowed={(can) => can(permission)}>{children}</AllowedIf>;
}

function AllowedIf({
  allowed,
  dialog,
  children,
}: {
  allowed(can: (p: Permission) => boolean): boolean;
  dialog?: boolean;
  children: ReactNode;
}) {
  const { can } = useSignedIn();
  const navigate = useNavigate();
  if (allowed(can)) return <>{children}</>;
  if (dialog) {
    return (
      <Dialog small title="Not allowed" onClose={() => navigate('/', { replace: true })}>
        <p className="note">{NOT_ALLOWED}</p>
      </Dialog>
    );
  }
  return <PageHead title="Not allowed" lede={NOT_ALLOWED} />;
}
